/**
 * audioPollAdapter - Normalizes the audio generation status response
 * into a generic `PollUpdate` consumed by GenerationContext.
 *
 * Self-registers on module load so that GenerationContext can resume
 * polling persisted audio jobs after a reload.
 */

import { VideoGenerationAPI } from '../api';
import { URLService } from '../url.service';
import type { AudioGenerationResponse } from '../../types';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

interface AudioStatusData {
  status?: string;
  progress?: number;
  message?: string;
  error?: string;
  result_url?: string;
  audio_path?: string;
  duration?: number;
  word_count?: number;
  script?: string;
  file_size?: number;
  metadata?: Record<string, unknown>;
  multilingual_audio_urls?: Record<string, string>;
  started_at?: string | null;
  completed_at?: string | null;
  elapsed_seconds?: number | null;
  estimated_remaining_seconds?: number | null;
}

export interface AudioJobResult {
  audioUrl: string;
  metadata: AudioGenerationResponse;
}

export const audioPollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<AudioJobResult>> => {
  const response = await VideoGenerationAPI.getAudioStatus(jobId);
  if (!response.success) {
    const rawPayload = response.data ?? { error_code: response.errorCode };
    return {
      done: true,
      status: 'failed',
      error: {
        message: response.error ?? 'Failed to get audio status',
        sanitized: sanitizeBackendError(rawPayload),
      },
    };
  }

  const data = (response.data ?? {}) as AudioStatusData;
  const terminal = data.status === 'completed' || data.status === 'error' || Boolean(data.error);

  const timing = {
    startedAt: data.started_at ?? null,
    elapsedSeconds: data.elapsed_seconds ?? null,
    estimatedRemainingSeconds: data.estimated_remaining_seconds ?? null,
  };

  if (!terminal) {
    return {
      done: false,
      status: 'running',
      progress: typeof data.progress === 'number' ? data.progress : undefined,
      stage: data.status,
      message: data.message,
      timing,
    };
  }

  if (data.status === 'completed' && data.result_url) {
    const baseUrl = URLService.getAPIBaseUrl();
    const absoluteUrl = data.result_url.startsWith('http')
      ? data.result_url
      : `${baseUrl}${data.result_url}`;
    const metadata: AudioGenerationResponse = {
      success: true,
      audio_url: absoluteUrl,
      audio_path: data.audio_path,
      duration: data.duration,
      word_count: data.word_count,
      script: data.script,
      file_size: data.file_size,
      metadata: data.metadata,
      generation_id: jobId,
      multilingual_audio_urls: data.multilingual_audio_urls,
      audio_languages: data.metadata?.audio_languages as string[] | undefined,
    };
    return {
      done: true,
      status: 'completed',
      progress: 100,
      stage: 'completed',
      result: { audioUrl: absoluteUrl, metadata },
      timing,
    };
  }

  return {
    done: true,
    status: 'failed',
    error: {
      message: data.error ?? data.message ?? 'Audio generation failed',
      sanitized: sanitizeBackendError(data),
    },
    timing,
  };
};

registerPollAdapter('audio', audioPollAdapter);
